import React from 'react';
import { 
  Video, 
  VideoOff, 
  Mic, 
  MicOff, 
  MessageCircle, 
  UserPlus, 
  Phone 
} from 'lucide-react';

/**
 * ActiveMeetingView - Full meeting view with video, controls, and participants
 */
const ActiveMeetingView = ({
  activeMeetingPlatform,
  isCameraOff,
  setIsCameraOff,
  isMicMuted,
  setIsMicMuted,
  mediaStream,
  setMediaStream,
  onLeaveMeeting,
  videoRef
}) => {
  const platformNames = {
    zoom: 'Zoom Meeting',
    teams: 'Microsoft Teams Meeting',
    meet: 'Google Meet',
    zoho: 'Zoho Meeting'
  };

  const platformColors = {
    zoom: 'bg-blue-500',
    teams: 'bg-purple-600',
    meet: 'bg-green-500',
    zoho: 'bg-orange-500'
  };

  const participants = [
    { name: 'Sarah M.', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=150&fit=crop' },
    { name: 'Michael C.', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=150&fit=crop' },
    { name: 'Emily R.', image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=150&fit=crop' },
    { name: 'David P.', image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=150&fit=crop' }
  ];

  const handleToggleMic = () => {
    setIsMicMuted(!isMicMuted);
    if (mediaStream) {
      const audioTrack = mediaStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = isMicMuted; // Toggle
      }
    }
  };

  const handleToggleCamera = () => {
    setIsCameraOff(!isCameraOff);
    if (mediaStream) {
      const videoTrack = mediaStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = isCameraOff; // Toggle
      }
    }
  };

  const handleLeave = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    onLeaveMeeting?.();
  };

  return (
    <div className="flex flex-col bg-black h-full overflow-hidden">
      {/* Meeting Header */}
      <div className="bg-gray-900 border-b border-gray-400/20 p-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${platformColors[activeMeetingPlatform] || 'bg-purple-500'} animate-pulse`}></div>
          <span className="text-white font-semibold text-sm">
            {platformNames[activeMeetingPlatform] || 'Meeting'}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const meetingId = prompt("Enter Meeting ID:");
              if (meetingId) {
                console.log("Meeting ID:", meetingId);
              }
            }}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2"
            data-testid="meeting-id-btn"
          >
            <UserPlus size={16} />
            Meeting ID
          </button>
          <button
            onClick={handleLeave}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-medium transition-colors"
            data-testid="leave-meeting-btn"
          >
            Leave Meeting
          </button>
        </div>
      </div>

      {/* Main Video Area */}
      <div className="flex-1 relative bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
        {/* Large Camera Feed */}
        <div className="absolute inset-0">
          {!isCameraOff ? (
            <video
              autoPlay
              muted
              playsInline
              ref={(video) => {
                if (videoRef) videoRef.current = video;
                if (video && !video.srcObject) {
                  navigator.mediaDevices.getUserMedia({ video: true, audio: true })
                    .then(stream => {
                      video.srcObject = stream;
                      setMediaStream?.(stream);
                      
                      const audioTrack = stream.getAudioTracks()[0];
                      if (audioTrack) {
                        audioTrack.enabled = !isMicMuted;
                      }
                    })
                    .catch(err => {
                      console.log('Camera access denied:', err);
                    });
                }
              }}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-900">
              <div className="text-center">
                <Video size={64} className="text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Camera is off</p>
              </div>
            </div>
          )}
        </div>

        {/* Meeting Info Overlay */}
        <div className="absolute top-3 left-3 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-lg">
          <p className="text-white font-medium text-sm">Meeting in Progress</p>
          <p className="text-xs text-gray-300">
            {isMicMuted ? '🔇 Mic Muted' : '🎤 Mic Active'} • {isCameraOff ? '📷 Camera Off' : '📹 Camera Active'}
          </p>
        </div>

        {/* Participant Video Windows */}
        <div className="absolute top-3 right-3 grid grid-cols-2 gap-2">
          {participants.map((participant, idx) => (
            <div 
              key={idx} 
              className="w-32 h-24 bg-gray-700 border-2 border-gray-400/30 rounded-lg overflow-hidden"
            >
              <div className="w-full h-full relative">
                <img
                  src={participant.image}
                  alt={participant.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-1 left-1 bg-black/70 px-2 py-0.5 rounded text-xs text-white">
                  {participant.name}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Meeting Controls */}
      <div className="bg-gray-900 border-t border-gray-400/20 p-3 flex-shrink-0">
        <div className="flex items-center justify-center gap-3">
          <button 
            onClick={handleToggleMic}
            className={`p-3 rounded-full transition-colors ${
              isMicMuted 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-gray-800 hover:bg-gray-700'
            }`}
            title={isMicMuted ? "Unmute Microphone" : "Mute Microphone"}
            data-testid="toggle-mic-btn"
          >
            {isMicMuted ? (
              <MicOff size={20} className="text-white" />
            ) : (
              <Mic size={20} className="text-white" />
            )}
          </button>
          <button 
            onClick={handleToggleCamera}
            className={`p-3 rounded-full transition-colors ${
              isCameraOff 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-gray-800 hover:bg-gray-700'
            }`}
            title={isCameraOff ? "Turn Camera On" : "Turn Camera Off"}
            data-testid="toggle-camera-btn"
          >
            {isCameraOff ? (
              <VideoOff size={20} className="text-white" />
            ) : (
              <Video size={20} className="text-white" />
            )}
          </button>
          <button 
            className="p-3 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors" 
            title="Chat"
            data-testid="meeting-chat-btn"
          >
            <MessageCircle size={20} className="text-white" />
          </button>
          <button 
            className="p-3 bg-gray-800 hover:bg-gray-700 rounded-full transition-colors" 
            title="Invite"
            data-testid="meeting-invite-btn"
          >
            <UserPlus size={20} className="text-white" />
          </button>
          <button
            onClick={handleLeave}
            className="p-3 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
            title="End Call"
            data-testid="end-call-btn"
          >
            <Phone size={20} className="text-white rotate-[135deg]" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveMeetingView;
